import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentLifecycleImage } from "@/api/admin-api"

import { Rocket } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"

import { parseHostedAgentImageRef } from "../utils/hosted-agent-lifecycle"

/**
 * "Upgrade now" (B13): collect the target image. Only an approved digest is
 * an upgrade. The caller queues the lifecycle request the VM worker executes.
 */
export function HostedAgentUpgradeDialog({
  isOpen,
  isPending,
  agentName,
  currentVersion,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean
  isPending: boolean
  agentName: string
  currentVersion: string | null
  onOpenChange: (open: boolean) => void
  onConfirm: (image: HostedAgentLifecycleImage) => Promise<void>
}) {
  const { t } = useTranslation()
  const [imageRef, setImageRef] = useState("")
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setImageRef("")
      setTouched(false)
    }
  }, [isOpen])

  const parsed = parseHostedAgentImageRef(imageRef)
  const showError = touched && parsed.error !== null

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setTouched(true)
    if (!parsed.image) return
    await onConfirm(parsed.image)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isPending && onOpenChange(open)}>
      <DialogContent className="sm:max-w-lg">
        <form className="contents" onSubmit={(event) => void handleSubmit(event)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="size-5" />
              {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.title", {
                defaultValue: "Upgrade {{name}}",
                name: agentName,
              })}
            </DialogTitle>
            <DialogDescription>
              {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.description", {
                defaultValue:
                  "The deploy worker on the customer VM pauses the agent, drains active runs, recreates the container from the new image, verifies it (health, handshake, self-test) and resumes. A failed verification rolls back automatically.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.currentVersion", {
                defaultValue: "Current version: {{version}}",
                version:
                  currentVersion ??
                  t("adminDashboard.hostedAgents.hermesNeverSeen", { defaultValue: "Never seen" }),
              })}
            </p>
            <div className="space-y-2">
              <Label htmlFor="hosted-agent-upgrade-image">
                {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.imageLabel", {
                  defaultValue: "Image digest",
                })}
              </Label>
              <Input
                aria-invalid={showError || undefined}
                autoComplete="off"
                disabled={isPending}
                id="hosted-agent-upgrade-image"
                spellCheck={false}
                value={imageRef}
                placeholder={t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.placeholder", {
                  defaultValue: "sha256:…",
                })}
                onBlur={() => setTouched(true)}
                onChange={(event) => setImageRef(event.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.imageHelp", {
                  defaultValue:
                    "Accepts sha256:<64 hex> or helloai-hermes@sha256:<64 hex>. A digest is immutable and gives an exact rollback target.",
                })}
              </p>
              {showError ? (
                <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
                  {parsed.error === "required"
                    ? t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.imageRequired", {
                        defaultValue: "Enter the image to upgrade to.",
                      })
                    : t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.imageInvalid", {
                        defaultValue: "Not a valid digest (sha256 + 64 hex characters).",
                      })}
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button
              disabled={isPending}
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.cancel", {
                defaultValue: "Cancel",
              })}
            </Button>
            <Button disabled={isPending || !parsed.image} type="submit">
              {isPending ? <Spinner className="size-4" /> : <Rocket className="size-4" />}
              {t("adminDashboard.hostedAgents.lifecycle.upgradeDialog.confirm", {
                defaultValue: "Queue upgrade",
              })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
